/* Canonical fact-sheet schema for the forecast comparison feature. Mirror of
   api/src/services/forecastTypes.ts — keep the two in sync. */

export interface ForecastRouting {
  workstreamId: string | null;
  workstreamName: string | null;
  confidence: number;
  routed: boolean;
}

export interface ForecastMappedParticipant {
  email: string;
  entity: string | null;
  person: string | null;
  role: string | null;
  confirmed: boolean;
}

export interface ForecastParticipants {
  mapped: ForecastMappedParticipant[];
  unknown: string[];
}

export interface ForecastMemoField {
  value: string;
  source: string | null;
}

export interface ForecastMemoWorkstream {
  phase: string | null;
  fields: Record<string, ForecastMemoField>;
}

export interface ForecastDocRegisterEntry {
  docType: string;
  name: string;
  currentVersion: number;
}

export interface ForecastDocFunnel {
  triage: Array<{ filename: string; classification: "updated" | "substantive" | "noise" }>;
  relevance: Array<{ filename: string; relevant: boolean }>;
  delta: Array<{
    clause: string;
    before: string | null;
    after: string | null;
    severity: "High" | "Medium" | "Low";
  }>;
  maxSeverity: "High" | "Medium" | "Low" | "None";
}

export interface ForecastFlags {
  anomaly: { detected: boolean; kind: string | null };
  privilege: { privileged: boolean; waiverRisk: string | null };
  phase: { newPhase: string | null };
  newParticipants: Array<{
    email: string;
    entityName: string | null;
    kind: string | null;
    role: string | null;
  }>;
  conflicts: Array<{
    field: string;
    oldValue: string;
    newValue: string;
    resolution: "supersede" | "conflict" | "restate";
  }>;
}

export interface ForecastEscalation {
  triggered: boolean;
  recommendation: string | null;
  urgency: "high" | "medium" | "low" | null;
}

export interface ForecastTrackers {
  timeline: Array<{ date: string; event: string; kind: string }>;
  issues: Array<{ issue: string; status: string; owner: string | null }>;
  commitments: Array<{
    who: string;
    what: string;
    due: string | null;
    direction: string;
    status: string;
  }>;
  diligence: Array<{ item: string; status: string; party: string | null }>;
  wires: Array<{ amount: string; from: string; to: string; status: string }>;
  signatures: Array<{ document: string; party: string; status: string }>;
  closingItems: Array<{ condition: string; status: string; evidence: string | null }>;
}

export interface ForecastCrossChecks {
  dispatch: Array<{ document: string; action: string; owner: string | null; reason: string | null }>;
  docChanges: Array<{ document: string; section: string | null; change: string; basis: string | null }>;
  fidelity: Array<{ field: string; memoValue: string; docValue: string; document: string }>;
  discrepancies: Array<{
    field: string;
    memoValue: string;
    statedValue: string;
    speaker: string | null;
    severity: string;
  }>;
}

export interface ForecastStateOfPlay {
  phase: string | null;
  momentum: "positive" | "steady" | "stalled" | "concerning" | null;
  tracking: "on_track" | "at_risk" | "behind" | null;
  briefing: string | null;
  nextActions: Array<{ action: string; who: string | null }>;
}

export interface ForecastApproval {
  type: string;
  severity: string;
  detail: string | null;
}

/** Cumulative fact-sheet snapshot after one email (1-indexed local id). */
export interface ForecastSnapshot {
  emailId: number;
  routing: ForecastRouting;
  participants: ForecastParticipants;
  memo: Record<string, ForecastMemoWorkstream>;
  documentRegister: Record<string, ForecastDocRegisterEntry[]>;
  docFunnel: ForecastDocFunnel;
  flags: ForecastFlags;
  escalation: ForecastEscalation;
  trackers: Record<string, ForecastTrackers>;
  crossChecks: ForecastCrossChecks;
  stateOfPlay: ForecastStateOfPlay;
  approvals: ForecastApproval[];
  /** Section keys actually present in the uploaded forecast — absent sections
   *  are "no prediction" and excluded from scoring. Always full for actuals. */
  predictedSections?: SectionKey[];
}

export const SECTION_KEYS = [
  "routing",
  "participants",
  "memo",
  "documentRegister",
  "docFunnel",
  "flags",
  "escalation",
  "trackers",
  "crossChecks",
  "stateOfPlay",
  "approvals",
] as const;

export type SectionKey = (typeof SECTION_KEYS)[number];

export const SECTION_LABELS: Record<SectionKey, string> = {
  routing: "Routing",
  participants: "Participants",
  memo: "Memo",
  documentRegister: "Documents",
  docFunnel: "Doc Funnel",
  flags: "Flags",
  escalation: "Escalation",
  trackers: "Trackers",
  crossChecks: "Cross-checks",
  stateOfPlay: "State of Play",
  approvals: "Approvals",
};

/** Forecast-panel accordion order (mirrors right-column tab order). */
export const PANEL_SECTION_ORDER: SectionKey[] = [
  "routing",
  "participants",
  "memo",
  "documentRegister",
  "trackers",
  "flags",
  "escalation",
  "crossChecks",
  "stateOfPlay",
  "approvals",
  "docFunnel",
];

/* ---- comparison output ---- */

export interface DeltaEntry {
  path: string;
  forecast: string | null;
  actual: string | null;
  kind: "match" | "mismatch" | "missing" | "extra";
}

export interface SectionDiff {
  section: SectionKey;
  /** 0–1; 1 = perfect match. Null when not comparable (empty or no forecast). */
  score: number | null;
  matches: number;
  total: number;
  deltas: DeltaEntry[];
  /** Section was absent from the uploaded forecast — excluded from scoring. */
  noForecast?: boolean;
}

export interface EmailDiff {
  emailId: number;
  overallScore: number;
  sections: SectionDiff[];
}

export interface ForecastComparisonDoc {
  forecastRunId: string;
  simulationRunId: string;
  emailDiffs: EmailDiff[];
  summary: {
    overallScore: number;
    sectionAverages: Record<string, number>;
    perfectMatches: number;
    majorDivergences: number;
  };
}

export interface ForecastRunSummary {
  id: string;
  name: string | null;
  generatedBy: string | null;
  uploadedAt: string;
  snapshotCount: number;
}

export function emptySnapshot(emailId: number): ForecastSnapshot {
  return {
    emailId,
    routing: { workstreamId: null, workstreamName: null, confidence: 0, routed: false },
    participants: { mapped: [], unknown: [] },
    memo: {},
    documentRegister: {},
    docFunnel: { triage: [], relevance: [], delta: [], maxSeverity: "None" },
    flags: {
      anomaly: { detected: false, kind: null },
      privilege: { privileged: false, waiverRisk: null },
      phase: { newPhase: null },
      newParticipants: [],
      conflicts: [],
    },
    escalation: { triggered: false, recommendation: null, urgency: null },
    trackers: {},
    crossChecks: { dispatch: [], docChanges: [], fidelity: [], discrepancies: [] },
    stateOfPlay: { phase: null, momentum: null, tracking: null, briefing: null, nextActions: [] },
    approvals: [],
  };
}
